import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import NumeratedTicketsEntry from './NumeratedTicketsEntry';
import NumeratedOrderDetails from './NumeratedOrderDetails';
import RenderStageMap from './RenderStageMap';
import isMobile from '../utils/isMobile';
import { useFreeSeatMutation, useGetSeatsInfoQuery } from '../store/apis/events';
import { DisplaySeatDTO } from '../models/seat';
import { toastError } from '../utils/toasts';
import EventDTO from '../models/event';
import { useCreateReservationMutation, useGetReservationAndSeatsQuery } from '../store/apis/tickets';
import { setReservation } from '../store/slices/orders';
import { RootState, useCheckCouponValidationMutation } from '../store';
import TicketDTO from '../models/ticket';
import useIsBackOffice from '../hooks/useIsBackoffice';

interface NumeratedSeatsProps {
  event: EventDTO;
  businessId: string;
  selectedSeats: DisplaySeatDTO[];
  onSelectedSeatsChange: (seats: DisplaySeatDTO[]) => void;
  tickets: TicketDTO[];
}

const NumeratedSeats = ({
  event, businessId, selectedSeats, onSelectedSeatsChange, tickets,
}: NumeratedSeatsProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isBackOffice = useIsBackOffice();
  const [freeSeat, freeSeatResults] = useFreeSeatMutation();
  const shouldRefetch = location.state?.shouldRefetch ?? false;
  const reservationCreatedRef = useRef(false);
  const { data: displaySeats, refetch: refetchSeats } = useGetSeatsInfoQuery({
    eventId: event.id,
    businessId,
  });
  const reservation = useSelector((state: RootState) => state.orders.reservation);
  const { data: reservationAndSeats, refetch: refetchReservationAndSeats } = useGetReservationAndSeatsQuery({
    businessId: event?.business_id ?? '',
    eventId: event?.id ?? '',
    reservationCode: reservation?.code ?? '',
  }, { skip: !reservation || !event });
  const [createReservation, { data: reservationCreated }] = useCreateReservationMutation();
  const [hasCheckedReservation, setHasCheckedReservation] = useState(false);
  const [displayTickets, setDisplayTickets] = useState<TicketDTO[]>([]);
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [checkCouponValidation, couponValidationResults] = useCheckCouponValidationMutation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const coupon = params.get('coupon');
    setCouponCode(coupon);
  }, [location.search]);

  const validateCoupon = (codeVal: string) => {
    checkCouponValidation({
      businessId: event?.business_id || '',
      eventId: event?.id || '',
      amount: 0,
      couponCode: codeVal,
    });
  };

  useEffect(() => {
    if (couponCode) {
      validateCoupon(couponCode);
    }
  }, [couponCode]);

  useEffect(() => {
    if (tickets) {
      setDisplayTickets([...tickets].sort((a, b) => a.price - b.price).filter((t) => (isBackOffice ? t.event_sector : t.event_sector && t.public_sale_available)));
    }
  }, [tickets, isBackOffice]);

  const handleSeatsSelected = async (seats: DisplaySeatDTO[]) => {
    const removedSeats = selectedSeats.filter(
      (prevSeat) => !seats.some((newSeat) => newSeat.id === prevSeat.id),
    );
    const seatToRemove = removedSeats[0] ?? null;

    if (seatToRemove && reservation?.code && event) {
      try {
        await freeSeat({
          businessId: event.business_id,
          eventId: event.id,
          reservationCode: reservation.code,
          eventSeatId: seatToRemove.id,
        });
        onSelectedSeatsChange(seats);
      } catch (error) {
        console.error('Error freeing seat:', error);
        toastError('Error al liberar la butaca');
      }
    }
    if (!seatToRemove) {
      onSelectedSeatsChange(seats);
    }
  };

  useEffect(() => {
    setHasCheckedReservation(true);
  }, []);

  useEffect(() => {
    if (event && hasCheckedReservation && (!reservation || reservation.event_id !== event.id) && !reservationCreatedRef.current) {
      reservationCreatedRef.current = true;
      createReservation({
        businessId: event.business_id,
        eventId: event.id,
      });
    } else if (event && hasCheckedReservation && reservation && reservation.event_id === event.id && reservationAndSeats) {
      if (reservationAndSeats) {
        onSelectedSeatsChange(reservationAndSeats.reservation_seats);
      }
    }
  }, [event, reservation, hasCheckedReservation, createReservation, reservationAndSeats, onSelectedSeatsChange]);

  useEffect(() => {
    if (reservationCreated?.data?.code) {
      dispatch(setReservation(reservationCreated.data));
    }
  }, [reservationCreated, dispatch]);

  useEffect(() => {
    refetchSeats();
  }, []);

  const hasHandledRefetch = useRef(false);

  useEffect(() => {
    if (shouldRefetch && reservation && event && !hasHandledRefetch.current) {
      hasHandledRefetch.current = true;
      refetchSeats();
      refetchReservationAndSeats();
      navigate(location.pathname, { replace: true, state: { ...location.state, shouldRefetch: false } });
    }
  }, [shouldRefetch, reservation, event, refetchSeats, refetchReservationAndSeats, navigate, location]);

  return (
    <div className='flex flex-col md:flex-row gap-4'>
      <div className='w-full'>
        {displaySeats && (
          <div className={`${isMobile() ? 'px-6' : ''}`}>
            <RenderStageMap
              seatsInfo={displaySeats}
              selectedSeats={selectedSeats}
              onSeatsUpdate={handleSeatsSelected}
              venue={event?.venue}
              tickets={tickets}
            />
          </div>
        )}
      </div>

      <div className='w-full'>
        <NumeratedTicketsEntry tickets={displayTickets} coupon={couponValidationResults.data?.data?.coupon ?? undefined} />
        <div className='mt-5'>
          <NumeratedOrderDetails
            displaySeats={selectedSeats}
            onSeatsUpdate={handleSeatsSelected}
            isLoading={freeSeatResults.isLoading}
            coupon={couponValidationResults.data?.data?.coupon ?? undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default NumeratedSeats;
